'use client';

import { useEffect } from 'react';

/**
 * Error boundary for the dashboard route group.
 *
 * Without one here, a throw in any dashboard route unwinds to the root
 * `app/error.tsx` and replaces the whole shell, so a single failing route takes
 * the navigation and chrome with it. Scoping the boundary to the group keeps the
 * failure inside the routed content.
 *
 * Deliberately built from a plain button and no shared UI imports: an error
 * boundary that pulls in the component tree it is meant to catch can fail while
 * rendering the fallback.
 */
export default function DashboardError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('[DashboardLayout] Error:', error);
  }, [error]);

  return (
    <div className="p-4 w-full pt-2 bg-ds-surface-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 mt-20 flex flex-col items-center gap-4">
        <p className="ds-text-body-sm-semibold text-ds-text-default">
          Something went wrong loading this page.
        </p>
        <button
          type="button"
          onClick={reset}
          className="ds-text-body-xs-medium text-ds-text-default border border-ds-border-subtlest rounded-md h-8 px-4 hover:bg-ds-gray-subtlest transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}
